import React, {Component} from 'react';
import {connect} from 'react-redux';
import Auth from '../../auth/auth';

/**
 * The holder-facing transitional-access expiry banner.
 *
 * docs/access/slice-6a2-holder-banner-plan.md §3.6, drawn as S9 in
 * docs/mockups/legacy-super-expiry-banner.html (rev 4, approved 2026-10-16).
 *
 * PLACEMENT A: app-wide, under the top bar, rendered once by <SecuredLayout>. The holder sees it
 * while doing the work they are about to lose the ability to do, rather than on a settings page
 * they have no reason to open.
 *
 * TWO POPULATIONS, and conflating them is the defect this component exists to avoid:
 *
 *   legacy_super  the ROLE, which is `*`. Headline names Super User access. No enumeration —
 *                 listing the losses under a wildcard is meaningless.
 *   parity        single PERMISSIONS time-boxed by the backfill on data_custodian/data_steward.
 *                 These holders NEVER had Super User access and keep their own role, so the
 *                 headline is different and the losses ARE enumerated — from `lost`, which the
 *                 server has already reduced to what is genuinely lost.
 *
 * NOTHING IS COMPUTED HERE. `kind`, the date, and the loss list all arrive decided from
 * `GET /access/permissions/me`; this renders them. A client-side set difference would be a second
 * implementation of the rule that §1 of the plan spent three revisions getting right.
 *
 * Class component + connect(), per CLAUDE.md — the React Flow hooks exception does not apply.
 */
class ExpiryBanner extends Component {
  constructor(props) {
    super(props);
    this.auth = new Auth();
    // Dismissal is per-DAY and client-only: the cost of losing it is the banner coming back, which
    // is the intended behaviour anyway. Deliberately NOT durable — see the plan's §3.2 note on why
    // "once" was expensive enough to drop a whole state.
    this.state = {dismissedOn: localStorage.getItem('acExpiryBannerDismissedOn')};
  }

  dismiss = () => {
    const today = new Date().toDateString();
    localStorage.setItem('acExpiryBannerDismissedOn', today);
    this.setState({dismissedOn: today});
  };

  render() {
    // The guard lives in Auth: a payload left behind by a FAILED refresh can be past-dated, and
    // rendering it would be the post-expiry message the user decided against.
    const banner = this.auth.getAcExpiringAuthority();
    if (!banner) return null;

    const expires = new Date(banner.date);
    const days = Math.ceil((expires.getTime() - Date.now()) / 86400000);
    const urgent = days <= 3;

    // The red state has NO dismiss control, and the amber state's dismissal lasts one day.
    if (!urgent && this.state.dismissedOn === new Date().toDateString()) return null;

    // 'en-GB', NOT the runtime locale. `undefined` rendered "October 29, 2026" in the §4.4 visual
    // pass, while the approved mockup reads "18 January 2027" — so the copy that shipped would
    // have depended on the viewer's machine, and would not have been the copy anyone signed off.
    const when = expires.toLocaleDateString('en-GB', {day: 'numeric', month: 'long', year: 'numeric'});
    const legacy = banner.kind === 'legacy_super';

    // Display names, baseline already filtered by the controller. Pluralised, because the live
    // payload had TWO roles and the singular read "Your data_champion, data_custodian role".
    const names = banner.survivingRoleNames || [];
    const roles = names.length > 1 ?
      `Your ${names.slice(0, -1).join(', ')} and ${names[names.length - 1]} roles and everything they carry` :
      names.length === 1 ? `Your ${names[0]} role and everything it carries` : '';

    return (
      <div className={`ac-expiry-banner${urgent ? ' ac-expiry-banner--urgent' : ''}`}
        role="status" data-testid="ac-expiry-banner">
        <span className="ac-expiry-banner__icon" aria-hidden="true">!</span>
        <div className="ac-expiry-banner__body">
          <div className="ac-expiry-banner__title">
            {legacy ? 'Your Super User access ends on ' : 'Some of your permissions end on '}
            <span data-testid="ac-expiry-date">{when}</span>
            {days > 0 && ` — ${days} ${days === 1 ? 'day' : 'days'} from now`}
          </div>

          <p className="ac-expiry-banner__lead">
            {legacy ?
              'This is the temporary access you were given when the new permissions system switched on, so that nothing stopped working on the first day. It was always time-limited, and it cannot be extended by you.' :
              'These were granted when the new permissions system switched on, so that nothing stopped working on the first day. They were always time-limited, and they cannot be extended by you.'}
          </p>

          <div className="ac-expiry-banner__split">
            <div className="ac-expiry-banner__half ac-expiry-banner__half--keep">
              <p className="ac-expiry-banner__label">You keep</p>
              <ul data-testid="ac-expiry-keeps">
                {legacy ? [
                  <li key="read">Reading the data dictionary, lineage and reports</li>,
                  <li key="own">The schemas you own</li>,
                  <li key="raise">Raising change requests</li>,
                ] : [
                  // Naming the role was approved in mockup rev 4 question 3.
                  <li key="role">{roles || 'Your existing role and everything it carries'}</li>,
                  <li key="raise">Raising change requests</li>,
                ]}
              </ul>
            </div>
            <div className="ac-expiry-banner__half ac-expiry-banner__half--lose">
              <p className="ac-expiry-banner__label">You lose</p>
              <ul data-testid="ac-expiry-losses">
                {legacy ? [
                  <li key="approve">Approving change requests</li>,
                  <li key="manage">Managing users, roles and connections</li>,
                  <li key="conf">Viewing confidential objects outside what you own</li>,
                ] : (banner.lost || []).map((p) => <li key={p.code} data-code={p.code}>{p.label}</li>)}
              </ul>
            </div>
          </div>

          <p className="ac-expiry-banner__foot">
            An administrator has to assign these — the access roster already flags you.
          </p>
        </div>

        {!urgent && (
          <button type="button" className="ac-expiry-banner__dismiss" onClick={this.dismiss}
            aria-label="Dismiss until tomorrow" data-testid="ac-expiry-dismiss">×</button>
        )}
      </div>
    );
  }
}

// No store state is read — the payload is cached by Auth off /access/permissions/me, the same way
// getAcPermissions() is. connect() is here for the house pattern and so the banner can move onto
// Redux without changing its call site if the hint layer ever lands there.
export default connect(() => ({}))(ExpiryBanner);
export {ExpiryBanner};
